import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Skull, Leaf, Thermometer, Drop, Download, Envelope, Lightning, ShareNetwork, SpinnerGap, Flask, CaretDown, CaretUp, Briefcase, Sun, Truck, ChatCircleText, ArrowsClockwise, Clipboard, GraduationCap, CurrencyDollar } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { RelatedTools } from '../components/RelatedTools';

const BAND = {
  minimal: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400',
  low: 'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-400',
  moderate: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400',
  high: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400',
  unknown: 'bg-muted/30 border-border text-muted-foreground',
};

const GRASSES = [
  ['bermuda', 'Bermuda'],
  ['zoysia', 'Zoysia'],
  ['st_augustine', 'St. Augustine'],
  ['tall_fescue', 'Tall Fescue'],
  ['kentucky_bluegrass', 'Kentucky Bluegrass'],
  ['ryegrass', 'Ryegrass'],
  ['centipede', 'Centipede'],
  ['bahiagrass', 'Bahiagrass'],
];

const FungicideCheatSheetPage = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    zip: sp.get('zip') || '',
    grass_type: sp.get('grass') || 'bermuda',
    property_label: sp.get('label') || 'My Lawn',
    recipient: sp.get('to') || '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-run when ZIP present on mount (deep-link friendly)
  useEffect(() => {
    if (form.zip && form.zip.length >= 4) onCompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCompute = async () => {
    if (!form.zip || form.zip.length < 4) {
      toast.error('Enter a ZIP first.');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/fungicide/cheatsheet`, form);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate cheat sheet.');
    } finally { setLoading(false); }
  };

  const onDownloadPdf = async () => {
    if (!data) return;
    try {
      const r = await axios.post(`${API_URL}/api/fungicide/cheatsheet-pdf`, form, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fungicide-cheatsheet-${form.property_label.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF generation failed.');
    }
  };

  const onShareLink = () => {
    const params = new URLSearchParams({
      zip: form.zip,
      grass: form.grass_type,
      label: form.property_label,
      ...(form.recipient ? { to: form.recipient } : {}),
    });
    const url = `${window.location.origin}/fungicide-cheat-sheet?${params.toString()}`;
    navigator.clipboard.writeText(url);
    toast.success('Shareable link copied');
  };

  const onEmail = () => {
    if (!data) return;
    const to = '';
    const subject = `Fungicide Cheat Sheet — ${form.property_label}`;
    const body =
      `Hey${form.recipient ? ' ' + form.recipient : ''},\n\n` +
      `Quick cheat sheet for the lawn at ${form.property_label} (ZIP ${form.zip}) — live from TurfPulse:\n\n` +
      `🎯 Priority today: ${data.priority_disease_label}\n` +
      `📊 Dollar Spot pressure: ${data.pressure.dollar_spot.probability_pct}% (${data.pressure.dollar_spot.band}) — Smith-Kerns 2013\n` +
      `📊 Large Patch score: ${data.pressure.large_patch.score}/100 (${data.pressure.large_patch.band})\n` +
      `🌡  Soil temp (2"): ${data.weather_now.soil_temp_f}°F · RH ${data.weather_now.rh_pct}%\n\n` +
      `⏱  Timing: ${data.timing.urgency}\n` +
      `💊 Rate: ${data.timing.rate_band}\n` +
      `🔁 Reapp window: ${data.timing.days_to_reapp} days\n\n` +
      `Rotation-safe picks (3 distinct FRAC groups):\n` +
      data.rotation.map((r, i) => `${i + 1}. ${r.name} (${r.active_ingredient}) — FRAC ${r.frac_group} — ${r.rate} — reapp ${r.reapp_days}d`).join('\n') +
      `\n\nRotation rule: ${data.rotation_rule}\n\n` +
      `Full PDF attached separately. Live data: ${window.location.origin}/fungicide-cheat-sheet?zip=${form.zip}\n\n— Derwin / TurfPulse`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="fungicide-cheatsheet-page">
      <SEO title="Fungicide Cheat Sheet — TurfPulse" description="Live dollar spot + large patch pressure, rotation-safe FRAC picks, rate + timing. Print or text to your crew." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="fcs-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold">Fungicide Cheat Sheet</span>
          <div className="w-8" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <Flask size={12} weight="fill" /> Smith-Kerns 2013 · FRAC rotation · label rates
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            One-page fungicide answer. <span className="text-primary">Live from your ZIP.</span>
          </h1>
          <p className="text-base text-muted-foreground">
            Pull today's dollar spot + large patch pressure, pick 3 rotation-safe products from the TurfPulse catalog, and text or email the cheat sheet to whoever's asking.
          </p>
        </div>

        {/* Inputs */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
            <Field label="ZIP" required>
              <Input value={form.zip} onChange={(e) => setField('zip', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="78664" className="font-mono" data-testid="fcs-zip-input" />
            </Field>
            <Field label="Grass">
              <Select value={form.grass_type} onValueChange={(v) => setField('grass_type', v)}>
                <SelectTrigger data-testid="fcs-grass-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRASSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Property name">
              <Input value={form.property_label} onChange={(e) => setField('property_label', e.target.value)} data-testid="fcs-label-input" />
            </Field>
            <Field label="For (optional)">
              <Input value={form.recipient} onChange={(e) => setField('recipient', e.target.value)} placeholder="GTLL" data-testid="fcs-recipient-input" />
            </Field>
            <Button onClick={onCompute} disabled={loading} data-testid="fcs-compute-btn">
              {loading ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <Lightning size={14} className="mr-1" weight="fill" />}
              Pull pressure
            </Button>
          </CardContent>
        </Card>

        {/* Result preview */}
        {data && (
          <>
            {/* Pressure tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="fcs-pressure-tiles">
              <Tile icon={<Skull size={14} weight="fill" />} label="Dollar Spot" value={`${data.pressure.dollar_spot.probability_pct ?? '—'}%`} band={data.pressure.dollar_spot.band} sub="Smith-Kerns 2013" testid="fcs-tile-ds" />
              <Tile icon={<Leaf size={14} weight="fill" />} label="Large Patch" value={data.pressure.large_patch.score ? `${data.pressure.large_patch.score}/100` : '—'} band={data.pressure.large_patch.band} sub="heuristic" testid="fcs-tile-lp" />
              <Tile icon={<Thermometer size={14} weight="fill" />} label="Soil temp" value={data.weather_now.soil_temp_f != null ? `${data.weather_now.soil_temp_f}°F` : '—'} sub="2-inch depth" testid="fcs-tile-soil" />
              <Tile icon={<Drop size={14} weight="fill" />} label="Humidity" value={data.weather_now.rh_pct != null ? `${data.weather_now.rh_pct}%` : '—'} sub="now" testid="fcs-tile-rh" />
            </div>

            {/* Priority verdict */}
            <Card className={`border-2 ${data.pressure.dollar_spot.band === 'high' || data.pressure.large_patch.band === 'high' ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="fcs-verdict-card">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Priority today</div>
                    <div className="text-xl font-bold">{data.priority_disease_label}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Timing</div>
                    <div className="text-sm font-semibold">{data.timing.urgency}</div>
                    <div className="text-[11px] text-muted-foreground">Reapp: {data.timing.days_to_reapp} days · {data.timing.rate_band}</div>
                  </div>
                </div>
                {data.summary && <p className="text-xs text-muted-foreground italic border-t border-border/40 pt-2">{data.summary}</p>}
              </CardContent>
            </Card>

            {/* Rotation */}
            <Card data-testid="fcs-rotation-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Rotation-safe picks (3 distinct FRAC groups)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.rotation.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 border border-border/60 rounded-md p-3" data-testid={`fcs-rot-${i}`}>
                    <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <div className="font-semibold text-sm">{r.name}</div>
                          <div className="text-[11px] text-muted-foreground">{r.active_ingredient} · {r.manufacturer}</div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono">FRAC {r.frac_group}</span>
                          <span className="font-mono">{r.rate}</span>
                          <span className="text-muted-foreground">· reapp {r.reapp_days}d</span>
                        </div>
                      </div>
                      {r.affiliate_link && (
                        <a href={r.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer" className="text-[11px] text-primary underline hover:no-underline inline-block mt-1" data-testid={`fcs-rot-link-${i}`}>
                          Open product on Amazon →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground italic border-t border-border/30 pt-2 mt-2">⚠ <strong>Rotation rule:</strong> {data.rotation_rule}</p>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button className="flex-1" onClick={onDownloadPdf} data-testid="fcs-pdf-btn">
                <Download size={16} className="mr-1.5" /> Download PDF
              </Button>
              <Button variant="outline" className="flex-1" onClick={onEmail} data-testid="fcs-email-btn">
                <Envelope size={16} className="mr-1.5" /> Email draft
              </Button>
              <Button variant="outline" className="flex-1" onClick={onShareLink} data-testid="fcs-share-btn">
                <ShareNetwork size={16} className="mr-1.5" /> Copy shareable link
              </Button>
            </div>

            <p className="text-[11px] text-muted-foreground text-center">
              Not a substitute for reading the product label. Always follow label rates, re-entry intervals, and PPE. Amazon links are affiliates; TurfPulse earns on qualifying purchases at no extra cost to you.
            </p>
          </>
        )}

        {/* Always-on Pro Workflow trainer — shows whether or not a sheet is loaded */}
        <ProWorkflowPanel />
      </main>
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl pb-12">
        <RelatedTools currentPath="/fungicide-cheat-sheet" />
      </div>
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'Fungicide Cheat Sheet', path: '/fungicide-cheat-sheet' },
      ]} />
    </div>
  );
};

const Field = ({ label, children, required }) => (
  <div className="space-y-1">
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

const Tile = ({ icon, label, value, band, sub, testid }) => (
  <div className={`rounded-lg border p-3 ${band ? BAND[band] || BAND.unknown : 'bg-card border-border/60'}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      {band && <span className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{band}</span>}
    </div>
    <div className="text-xl font-bold font-mono mt-1.5">{value}</div>
    <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
  </div>
);

// ── Pro Workflow trainer panel ──────────────────────────────────────────────
// 6 use-cases pulled straight from real pro/crew workflows. Always rendered
// on /fungicide-cheat-sheet so a new applicator sees the "why" every time
// they pull pressure. Bookmarkable via #pro-workflow anchor.
const WORKFLOWS = [
  {
    id: 'morning-route',
    icon: Sun,
    color: 'text-amber-500',
    title: '1. Morning Route Pre-Check',
    time: '5 min, pre-route',
    scenario: '8 properties on today\'s board, each different ZIP.',
    steps: [
      'Pull the cheat sheet, ZIP-hop through each property (30 sec each).',
      'Glance at pressure band colors only — green/LOW = normal day; red/HIGH = re-route to spray first.',
      'Note which properties are moderate+ on a sticky note or truck tablet.',
    ],
    roi: '10 min of planning avoids 1 unnecessary truck roll. $30–50 saved per skipped app, compounded across a 5-day week.',
  },
  {
    id: 'on-property',
    icon: Truck,
    color: 'text-sky-500',
    title: '2. On-Property Diagnosis',
    time: 'Truck phone, 60 sec',
    scenario: 'Bermuda lawn with circular brown patch — dollar spot starting or drought stress?',
    steps: [
      'Open `turfpulse.app/fungicide-cheat-sheet?zip=<client ZIP>&grass=bermuda` on the truck phone.',
      'If Smith-Kerns is 40%+, it\'s dollar spot. If <20% AND no rain 14d, it\'s drought — recommend deep irrigation, not fungicide.',
      'Show the homeowner the live tile. Let the science do the selling.',
    ],
    roi: '"I\'ll think about it" homeowners convert to same-day spray sales. Pressure visualization closes 2–3× better than jargon.',
  },
  {
    id: 'client-comm',
    icon: ChatCircleText,
    color: 'text-emerald-500',
    title: '3. Client Communication & Justification',
    time: '30 sec per email',
    scenario: 'Skeptical client: "Why are you charging me $85 for a spray I can\'t see a problem for?"',
    steps: [
      'Hit "Download PDF" or "Email draft" on the cheat sheet.',
      'One-pager lands in their inbox: live Smith-Kerns % for their ZIP, timing window, 3 rotation-safe picks with rates.',
      'Close with: "Curative costs 3× preventive. We can wait, but you\'ll pay more in 2 weeks."',
    ],
    roi: 'Preventive-app close rate goes from ~40% to ~75% when you lead with data. That\'s pure margin — product cost is the same, perceived value is 2×.',
  },
  {
    id: 'frac-rotation',
    icon: ArrowsClockwise,
    color: 'text-violet-500',
    title: '4. FRAC Rotation Tracking (Per-Property)',
    time: 'Built-in, zero extra steps',
    scenario: 'Client\'s last app was Heritage (FRAC 11) 21 days ago. Dollar spot hot again today.',
    steps: [
      'Pull cheat sheet — it returns 3 picks across different FRAC groups.',
      'Cross-check with /resistance tracker showing the last app.',
      'Skip the FRAC 11 pick → pick the FRAC 3 or 12 alternative. TurfPulse flags it automatically if you try to stack.',
    ],
    roi: 'FRAC 11 resistance is the #1 cause of "the spray isn\'t working anymore" — that\'s lost accounts. Rotating protects years of recurring revenue.',
  },
  {
    id: 'compliance',
    icon: Clipboard,
    color: 'text-red-500',
    title: '5. State Record-Keeping (TX TDA/TCEQ)',
    time: 'Saves 3–5 min per app',
    scenario: 'Texas requires applicators to log: date, product, rate, site, weather, target pest.',
    steps: [
      'The PDF already captures: date, city, grass, dollar spot %, large patch score, soil temp, RH, product + rate.',
      'Save one PDF per app to a client folder (Dropbox/Drive/file cabinet).',
      'If audited → you have the app log + the agronomic justification. That\'s the gold standard.',
    ],
    roi: '10–15 hours saved per year across 200 apps. Audit-proof documentation. Zero chance of forgetting a log entry.',
  },
  {
    id: 'crew-training',
    icon: GraduationCap,
    color: 'text-primary',
    title: '6. New Applicator Onboarding',
    time: 'Week 1, not month 6',
    scenario: 'New tech doesn\'t know FRAC from RPM. Needs guardrails before hitting customer lawns.',
    steps: [
      'Rule #1: Before every spray, pull the cheat sheet for the property.',
      'Rule #2: Pick from the rotation list. Never stack the same FRAC group twice in a row.',
      'Rule #3: Follow label rates only. If it says 1–4 fl oz/1k sqft, stay inside that window.',
      'Rule #4: Log every app back into /resistance so the next pick is informed.',
    ],
    roi: 'New tech stops making resistance-inducing mistakes from day one. Cuts the "oh no, we killed a lawn\'s spray response" learning curve entirely.',
  },
];

const UPSELL_TIPS = [
  { k: 'Low pressure day?', v: 'Sell a preventive fertility + wetting agent app. "Saving the spray budget for when you need it."' },
  { k: 'High pressure + client not on program?', v: 'Sell onto program. "Your neighbor two streets over is covered — here\'s why I\'d do the same for you."' },
  { k: 'DIY homeowner asking for advice?', v: 'Send the cheat sheet with affiliate links. You earn commission whether they hire you or not.' },
  { k: 'Post-app proof of work?', v: 'Attach the cheat sheet + a timestamped zone photo to the invoice. Zero dispute leverage.' },
];

const ProWorkflowPanel = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/0 scroll-mt-20" id="pro-workflow" data-testid="fcs-pro-workflow">
      <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setOpen((v) => !v)} data-testid="fcs-pro-workflow-toggle">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Briefcase size={18} weight="fill" className="text-primary" />
            Pro Workflow — how to use this cheat sheet
          </span>
          <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
            {open ? 'Hide' : 'Show training'} {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          </span>
        </CardTitle>
        {!open && (
          <p className="text-xs text-muted-foreground mt-1">
            6 real workflows used by licensed applicators — morning route, on-property diagnosis, client justification, FRAC rotation, state compliance, crew onboarding.
          </p>
        )}
      </CardHeader>

      {open && (
        <CardContent className="space-y-4" data-testid="fcs-pro-workflow-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="fcs-workflows-grid">
            {WORKFLOWS.map((w) => {
              const Icon = w.icon;
              const isExp = expanded === w.id;
              return (
                <div
                  key={w.id}
                  className={`border rounded-md p-3 transition-colors bg-card ${isExp ? 'border-primary/50' : 'border-border/60'}`}
                  data-testid={`fcs-workflow-${w.id}`}
                >
                  <button
                    type="button"
                    className="w-full text-left flex items-start justify-between gap-2"
                    onClick={() => setExpanded(isExp ? null : w.id)}
                    data-testid={`fcs-workflow-toggle-${w.id}`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <Icon size={18} weight="fill" className={`shrink-0 mt-0.5 ${w.color}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">{w.title}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{w.time}</div>
                      </div>
                    </div>
                    {isExp ? <CaretUp size={14} weight="bold" className="shrink-0 text-muted-foreground" /> : <CaretDown size={14} weight="bold" className="shrink-0 text-muted-foreground" />}
                  </button>

                  {isExp && (
                    <div className="mt-3 space-y-2 text-xs border-t border-border/50 pt-3" data-testid={`fcs-workflow-body-${w.id}`}>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Scenario</div>
                        <p className="italic text-muted-foreground">{w.scenario}</p>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Workflow</div>
                        <ol className="list-decimal list-inside space-y-1 text-foreground">
                          {w.steps.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-md p-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-0.5 flex items-center gap-1">
                          <CurrencyDollar size={10} weight="bold" /> ROI
                        </div>
                        <p className="text-[11px]">{w.roi}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Upsell patterns */}
          <div className="border-t border-border/50 pt-3 space-y-2" data-testid="fcs-upsell-block">
            <div className="flex items-center gap-2">
              <CurrencyDollar size={14} weight="fill" className="text-emerald-500" />
              <span className="text-sm font-semibold">Upsell patterns crews should learn</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {UPSELL_TIPS.map((t, i) => (
                <div key={i} className="text-xs border border-border/50 rounded-md p-2.5 bg-muted/20" data-testid={`fcs-upsell-${i}`}>
                  <div className="font-semibold text-foreground mb-0.5">{t.k}</div>
                  <div className="text-muted-foreground">{t.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Rules of thumb footer */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-xs" data-testid="fcs-rules-footer">
            <div className="font-semibold text-amber-700 dark:text-amber-400 mb-1">🚧 Non-negotiables</div>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Read the label before every mixed tank. TurfPulse rates are a guide, not the rule — the label is the rule.</li>
              <li>Never stack the same FRAC group back-to-back. Especially FRAC 11 (QoI) — resistance-prone.</li>
              <li>Apply 4–9 AM. Wind &gt; 10 mph = skip. Rain within 4 hr = skip unless rainfast &lt; 1 hr.</li>
              <li>Every app logged in /resistance within 24 hr. No exceptions.</li>
              <li>When in doubt, text Derwin. Better to pause than to poison.</li>
            </ul>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default FungicideCheatSheetPage;
